/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import AmountToWords from "../../../../components/_utils/AmountToWords";
import CommonAlertCard from "../../../../components/_utils/CommonAlertCard/index.jsx";
import CommonAssignLabelPopup from "../../../../components/_utils/CommonPopups/CommonAssignLabelPopup/index.jsx";
import SmallPopup from "../../../../components/_utils/CommonSmallPopup/index.jsx";
import SmallPopupWithBulletPoints from "../../../../components/_utils/CommonSmallPopupWithBulletPoint";
import PrimaryStepper from "../../../../components/_utils/CommonSteppers/PrimaryStepper";
import CommonToast from "../../../../components/_utils/CommonToast";
import TopNavComponent from "../../../../components/_utils/CommonTopNav/TopNavComponent/index.jsx";
import SVGComponent from "../../../../components/_utils/SVGComponent";
import BeneficiaryAccountViewAllPopup from "../../../../components/Accounts/BeneficiaryAccount/BeneficiaryAccountViewAllPopup/index.jsx";
import Loader from "../../../../components/Loader/Index.jsx";
import BeneficiaryTransactionStepAccount from "../../../../components/Transactions/BeneficiaryTransaction/BeneficiaryTransactionCreate/BeneficiaryTransactionStepAccount/index.jsx";
import BeneficiaryTransactionStepAdditionalDetails from "../../../../components/Transactions/BeneficiaryTransaction/BeneficiaryTransactionCreate/BeneficiaryTransactionStepAdditionalDetails/index.jsx";
import BeneficiaryTransactionStepAmount from "../../../../components/Transactions/BeneficiaryTransaction/BeneficiaryTransactionCreate/BeneficiaryTransactionStepAmount/index.jsx";
import BeneficiaryTransactionStepConfirm from "../../../../components/Transactions/BeneficiaryTransaction/BeneficiaryTransactionCreate/BeneficiaryTransactionStepConfirm/index.jsx";
import BeneficiaryRecreateTxnStepAdditionalDetails from "../../../../components/Transactions/BeneficiaryTransaction/BeneficiaryTransactionRecreate/BeneficiaryRecreateTxnStepAdditionalDetails";
import BeneficiaryRecreateTxnStepAmountPaymentMethod from "../../../../components/Transactions/BeneficiaryTransaction/BeneficiaryTransactionRecreate/BeneficiaryRecreateTxnStepAmountPaymentMethod";
import BeneficiaryRecreateTxnStepSourceDestination from "../../../../components/Transactions/BeneficiaryTransaction/BeneficiaryTransactionRecreate/BeneficiaryRecreateTxnStepSourceDestination";
import BeneficiaryTransactionViewAllPopup from "../../../../components/Transactions/BeneficiaryTransaction/BeneficiaryTransactionViewAllPopup/index.jsx";
import { getAllBeneficiaryAccounts } from "../../../../redux/features/accounts/beneficiaryAccount/index.js";
import { ownerAccountsViewAll } from "../../../../redux/features/owner/ownerAllVerification/ownerAllVerification.js";
import {
  bankLookUp,
  getAllCategories,
  panLookUp,
  uploadFile,
} from "../../../../redux/features/sharedResources/sharedResources";
import {
  beneficiaryTransactionsCreate,
  beneficiaryTransactionsDetailsById,
  beneficiaryTransactionsRecreate,
  beneficiaryTransactionsViewAll,
} from "../../../../redux/features/transactions/beneficiaryTransactions/index.js";
import {
  getOutsideBankingHoursLabel,
  isOutsideBankingHours,
} from "../../../../utils/bankingHours";
import {
  GlobalConfiguration,
  IntervalConfiguration,
  PaginationConfiguration,
  PaymentMethodAnimationConfiguration,
  ScreenSizeConfiguration,
} from "../../../../utils/configurationProvider";
import { MONTHS } from "../../../../utils/dateConstants.js";
import globalText from "../../../../utils/globalText";
import Validation from "../../../../utils/validation.js";
import "./index.css";

/*                          CONSTANTS & PURE HELPERS                          */

const {
  lengthCheckZero: ZERO,
  lengthCheckOne: ONE,
  lengthCheckTwo: TWO,
  lengthCheckThree: THREE,
} = GlobalConfiguration;

const RTGS_MIN_AMOUNT = 200000;
const MOBILE_BREAKPOINT = 800;
const AUTOFILL_HOVER_DELAY = 600;
const RECREATE_SESSION_KEY = "recreate_ben";

const EMPTY_COPY_DATA = { title: "", data_type: "", value: "" };

const COMMON_STEPPER_CONFIG = {
  previousEnableBgColor: "var(--primary-nav-act)",
  previousDisableBgColor: "var(--text-disabled)",
  previousEnableTextColor: "var(--text-primary)",
  previousHoverOption: "white",
  nextHoverOption: "black",
};

const omit = (obj, ...keys) =>
  Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key)),
  );

const uniq = (...lists) => [...new Set(lists.flatMap((list) => list || []))];

const readSessionJson = (key, fallback) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const clearRecreateSession = () => {
  try {
    sessionStorage.removeItem(RECREATE_SESSION_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Resolve the transaction being recreated.
 * Priority: sessionStorage (set by the details page) -> router state.
 * Returns null unless a real txn_id is available, so we never end up
 * sending `txn_id: [null]` to the refactor API.
 */
const resolveRefactoredTxn = (routeState) => {
  const stored = readSessionJson(RECREATE_SESSION_KEY, null);
  const fromState = routeState?.type === "refactorFlow" ? routeState : null;

  const isStoredRefactor = stored?.type === "refactorFlow";
  const txnId =
    (isStoredRefactor ? stored?.txn_id : null) || fromState?.txn_id || null;

  return txnId ? { type: "refactorFlow", txn_id: txnId } : null;
};

const isValidHttpUrl = (url) =>
  /^(https?:\/\/)([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(:[0-9]{1,5})?(\/.*)?$/.test(
    url,
  );

/** Splits a flat label list into owner labels / other labels. */
const splitLabels = (allLabels = [], ownerLabels = []) => ({
  ownerLabels: allLabels.filter((label) => ownerLabels.includes(label)),
  labels: allLabels.filter((label) => !ownerLabels.includes(label)),
});

/** Amount -> words, singularising "rupees" for amounts above 0 and up to 1. */
const getAmountWords = (amount) => {
  const amountNumber = parseFloat(amount);
  const words = AmountToWords(amountNumber);
  return amountNumber > ZERO && amountNumber <= ONE
    ? words.replace("rupees", "rupee")
    : words;
};

/** "5 Jan, 2026" (display format). */
const formatUIDate = (date) => {
  if (!date) return "";
  const parsed = new Date(date);
  return `${parsed.getDate()} ${parsed.toLocaleString("en-US", { month: "short" })}, ${parsed.getFullYear()}`;
};

const handleKeyDown = (event) => {
  const isTextField =
    event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA";

  if (isTextField) {
    if (event.key === "Tab") event.preventDefault();
    return;
  }
  if (["ArrowLeft", "ArrowRight", "Tab"].includes(event.key)) {
    event.preventDefault();
  }
};

/** Whether a mandatory category field has been filled in. */
const isMandatoryValueFilled = ({ data_type: dataType, value }) => {
  switch (dataType) {
    case "multi_select_list":
      return Array.isArray(value) && value.length > ZERO;
    case "from_year_to_year":
      return !!(value?.from && value?.to);
    case "date_range":
      return !!(value?.start_date && value?.end_date);
    case "toggle":
      return value === true;
    default:
      return value !== "" && value !== undefined;
  }
};

/** Category config fields + entered values + upload responses -> API payload. */
const buildAdditionalDataPayload = (
  fields,
  additionalData = {},
  uploadResponses = [],
  hasImage = false,
) => {
  let uploadIndex = ZERO;

  return fields?.map((field) => {
    const value = additionalData[field?.name];

    if (value) {
      switch (field?.data_type) {
        case "numeric":
          return { ...field, value: Number(value) };
        case "upload":
          return hasImage
            ? {
                ...omit(field, "description"),
                value: uploadResponses[uploadIndex++]?.payload,
              }
            : { ...field, value };
        case "toggle":
          return { ...omit(field, "description"), value };
        default:
          return { ...field, value };
      }
    }

    return field?.data_type === "toggle" ? omit(field, "description") : field;
  });
};

/** Category config fields + entered values -> mergeCategory (drives validation + confirm step). */
const buildMergeCategory = (fields, additionalData = {}) =>
  fields?.map((field) => {
    const value = additionalData[field?.name];

    const hasValue =
      value !== undefined &&
      (Array.isArray(value) ? value.length > ZERO : value !== "");

    if (hasValue) {
      return {
        ...field,
        value: field?.data_type === "numeric" ? String(value) : value,
      };
    }

    switch (field?.data_type) {
      case "multi_select_list":
        return { ...field, value: [] };
      case "toggle":
        return { ...field, value: false };
      case "hyperlink":
      case "upload":
        return { ...field, value: "" };
      case "time":
        return field;
      default:
        return field?.mandatory !== "Y" ? { ...field, value: "" } : field;
    }
  });

/** Raw input from a field component -> value stored in additionalData. */
const toAdditionalDataValue = (dataType, value) => {
  switch (dataType) {
    case "month_year": {
      if (!(value?.month && value?.year)) return "";
      const monthNumber = String(MONTHS.indexOf(value.month) + ONE).padStart(
        2,
        "0",
      );
      return `${monthNumber}/${value.year}`;
    }

    case "from_year_to_year": {
      const toNumberOrNull = (v) =>
        v !== undefined && v !== "" ? Number(v) : null;
      return {
        from: toNumberOrNull(value?.from),
        to: toNumberOrNull(value?.to),
      };
    }

    case "date_range":
      return value?.start_date && value?.end_date
        ? { start_date: value.start_date, end_date: value.end_date }
        : null;

    case "numeric":
      return String(value)
        .replace(/\D/g, "")
        .slice(ZERO, GlobalConfiguration?.gstLength);

    case "quantity":
      return Number(value);

    default:
      return value;
  }
};

/*                                 COMPONENT                                  */

const BeneficiaryTransactionCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollContainerRef = useRef(null);
  const stepperHeightRef = useRef(null);
  const prevBeneAccountRef = useRef(null);
  const hasAutoFillInteracted = useRef(false);
  const labelActionDispatchedRef = useRef(false);

  const currentAccountDetails = location?.state;
  const isReminderFlow = location?.state?.flowType === "ReminderFlow";
  const reminderAmountFromState = location?.state?.txnDetails?.reminderAmount;

  const parsedReminderAmount = Number(
    String(reminderAmountFromState ?? "")
      .replace(/,/g, "")
      .trim(),
  );

  const hasReminderAmount =
    isReminderFlow &&
    reminderAmountFromState !== null &&
    reminderAmountFromState !== undefined &&
    reminderAmountFromState !== "" &&
    Number.isFinite(parsedReminderAmount) &&
    parsedReminderAmount > 0;

  const reminderSettleType = hasReminderAmount
    ? parsedReminderAmount > RTGS_MIN_AMOUNT
      ? "rtgs"
      : "neft"
    : "";

  // Transaction being recreated (from the details page / any other entry point).
  // Only set when a real txn_id exists, so the refactor API never gets [null].
  const [refactoredTxn] = useState(() => resolveRefactoredTxn(location?.state));
  const isRefactorFlow =
    refactoredTxn?.type === "refactorFlow" && !!refactoredTxn?.txn_id;

  const steps = [
    globalText?.SOURCE_AND_DESTINATION_ACCOUNT,
    globalText?.CATEGORY_AND_LABELS,
    globalText?.AMOUNT,
    globalText?.CONFIRM_AND_CREATE,
  ];
  const lastStepIndex = steps.length - ONE;

  /* -------------------------------- state -------------------------------- */
  // flow / navigation
  const [activeStep, setActiveStep] = useState(0);
  const [stepDirection, setStepDirection] = useState(null);
  const [isStepAnimationComplete, setIsStepAnimationComplete] = useState(true);
  const [isNextEnabled, setIsNextEnabled] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= MOBILE_BREAKPOINT,
  );
  const [stepperHeight, setStepperHeight] = useState();

  // submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [isTxnLoading, setIsTxnLoading] = useState(false);
  const [hasSimilarTransaction, setHasSimilarTransaction] = useState(false);

  // payment method / amount
  const [isNeft, setIsNeft] = useState(true);
  const [isRtgs, setIsRtgs] = useState(false);
  const [slideAnimation, setSlideAnimation] = useState(false);
  const [isAmountCopied, setIsAmountCopied] = useState(false);

  // account step
  const [isConfidential, setIsConfidential] = useState(false);
  const [openOwner, setOpenOwner] = useState(false);
  const [activeType, setActiveType] = useState("pan");
  const [remPan, setRemPan] = useState("");
  const [remIFSC, setRemIFSC] = useState("");
  const [remAccount, setRemAccount] = useState("");
  const [haveBankAssociate, setHaveBankAssociate] = useState(0);
  const [havePANAssociate, setHavePANAssociate] = useState(0);
  const [hasUserSelectedAccount, setHasUserSelectedAccount] = useState(
    !!currentAccountDetails?.id,
  );

  // additional-details step
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [additionalData, setAdditionalData] = useState({});
  const [quantities, setQuantities] = useState({});
  const [timeData, setTimeData] = useState({});
  const [startDate, setStartDate] = useState({});
  const [endDate, setEndDate] = useState({});
  const [hyperLink, setHyperLink] = useState({});
  const [selectedValue, setSelectedValue] = useState({});
  const [selectedValueMonth, setSelectedValueMonth] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [previewImages, setPreviewImages] = useState({});

  // recreate / autofill flow
  const [reCreateFlow, setReCreateFlow] = useState(false);
  const [autoFill, setAutoFill] = useState(false);
  const [isHoverEnabled, setIsHoverEnabled] = useState(true);
  const [openBar, setOpenBar] = useState(false);
  const [refactorDisplayStep, setRefactorDisplayStep] = useState(0);
  const [mappingData, setMappingData] = useState("");
  const [mappingCat, setMappingCat] = useState("");
  const [catSelect, setCatSelect] = useState(false);
  const [sourceSelect, setSourceSelect] = useState(false);
  const [accountSelect, setAccountSelect] = useState(false);
  const [isAutoFilledSource, setIsAutoFilledSource] = useState(false);
  const [initialRefactorAdditionalDetail, setInitialRefactorAdditionalDetail] =
    useState({});
  const [copyData, setCopyData] = useState(EMPTY_COPY_DATA);

  // popups
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const [isLabelLoading, setIsLabelLoading] = useState(false);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const [isConfirmLeavePopupOpen, setIsConfirmLeavePopupOpen] = useState(false);
  const [isBankingHoursPopupOpen, setIsBankingHoursPopupOpen] = useState(false);
  const [activeAccountPopup, setActiveAccountPopup] = useState(null);

  const [txnPayLoad, setTxnPayLoad] = useState({
    bankAccountName: currentAccountDetails?.name || "",
    beneAccountId: currentAccountDetails?.id || "",
    bankAccountIFSC: currentAccountDetails?.ifsc_code || "",
    bankAccountNo: currentAccountDetails?.bank_acc_no || "",
    categoryIds: currentAccountDetails?.categoryIds || "",
    amount: "",
    settleType: "",
    bankLogo: currentAccountDetails?.bank_name || "",
    pan: currentAccountDetails?.pan || "",
    ownerId: null,
    ownerAccountName: "",
    ownerBankIFSC: null,
    ownerBankBalance: 0,
    ownerBankLogo: null,
    ownerLastUpdated: null,
    ownerBankAccountNo: null,
    selectedCategory: null,
    mergeCategory: null,
    amountInWords: "",
    labels: currentAccountDetails?.labels || [],
    ownerLabels: [],
  });

  // account details seeded from the incoming route state
  const [category, setCategory] = useState(txnPayLoad?.categoryIds || []);
  const [accountNumber, setAccountNumber] = useState(
    txnPayLoad?.bankAccountNo || "",
  );
  const [IFSC, setIFSC] = useState(txnPayLoad?.bankAccountIFSC || "");

  const [selectedLabels, setSelectedLabels] = useState(() =>
    uniq(txnPayLoad.labels, txnPayLoad.ownerLabels),
  );
  const [availableLabels, setAvailableLabels] = useState(() =>
    uniq(txnPayLoad.labels, txnPayLoad.ownerLabels),
  );

  /* ------------------------------- selectors ------------------------------ */
  const categories = useSelector(
    (state) => state.sharedResources?.categoriesData?.categories,
  );
  const { panLookupData, bankLookupData } = useSelector(
    (state) => state.sharedResources,
  );
  const {
    beneficiaryTransactionsDetailsLoading,
    beneficiaryTransactionsDetailsData,
  } = useSelector((state) => state?.beneficiaryTransactions);

  /* ------------------------------- derived -------------------------------- */
  const txnData = beneficiaryTransactionsDetailsData?.transaction?.[0];
  const txnCategoryId = txnData?.category_id?.category_id;
  const txnCategoryName = txnData?.category_id?.category_name;

  const isWindow = window.innerWidth >= ScreenSizeConfiguration?.mobileScreen;
  const stepAnimationClass =
    stepDirection === "next" ? "animate-slide-next" : "animate-slide-prev";

  const shouldShowBankingHoursAlert =
    isOutsideBankingHours() && [ZERO, THREE].includes(activeStep);

  const shouldShowAssociatedAccounts =
    (haveBankAssociate > ONE || havePANAssociate > ONE) &&
    accountNumber &&
    IFSC;

  const allLabels = uniq(txnPayLoad?.ownerLabels, txnPayLoad?.labels);

  const safeCategories = Array.isArray(categories) ? categories : [];
  const unassignCategories = safeCategories.filter(
    (cat) => cat?.active === "Y",
  );
  const unassignlength = unassignCategories.filter(
    (unassignCategory) => !category?.includes(unassignCategory?.id),
  );
  const isCategorySelected = !!selectedCategory;
  const disableCategories = true;

  const shouldShowRecreatePanel =
    (isRefactorFlow || reCreateFlow) && !isReminderFlow;

  const canToggleReminderBar =
    (!activeAccountPopup && activeStep === 0) ||
    (!openOwner && activeStep === 2) ||
    activeStep === 1 ||
    activeStep === 3;

  /* ------------------------------ small helpers --------------------------- */
  const triggerStepAnimation = (direction) => {
    setStepDirection(direction);
    setIsStepAnimationComplete(false);
    setTimeout(
      () => setIsStepAnimationComplete(true),
      IntervalConfiguration?.intervalSemiMid,
    );
  };

  const changeStep = (stepDelta, direction) => {
    setActiveStep((prev) => Math.max(prev + stepDelta, ZERO));
    triggerStepAnimation(direction);
    setAutoFill(false);
    setOpenBar(false);
  };

  const showAmountLimitToast = (comparison) =>
    CommonToast({
      text: (
        <>
          Transaction amount should be {comparison} <br /> INR 2 lakhs.
        </>
      ),
      icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      variant: "var(--error)",
    });

  const resetAdditionalFields = () => {
    setAdditionalData({});
    setSelectedValue({ from: "Select", to: "Select" });
    setStartDate({});
    setEndDate({});
    setHyperLink({});
    setPreviewImages({});
    setUploadedFiles({});
    setTimeData({});
    setQuantities({});
    setSelectedValueMonth({});
  };

  const clearSelectedCategory = () => {
    setSelectedCategory(null);
    setTxnPayLoad((prev) => ({
      ...prev,
      selectedCategory: null,
      mergeCategory: null,
    }));
  };

  const applySelectedCategory = (categoryData) => {
    setSelectedCategory(categoryData);
    setTxnPayLoad((prev) => ({
      ...prev,
      selectedCategory: categoryData,
      mergeCategory: categoryData?.configuration?.data || [],
    }));
  };

  const resetCopyData = () => setCopyData(EMPTY_COPY_DATA);

  /* ------------------------------ create / next --------------------------- */
  const buildDuplicateCheckParams = () => ({
    page_no: ONE,
    page_item_count: PaginationConfiguration?.paginationSmall,
    amount: txnPayLoad?.amount,
    payment_method: txnPayLoad?.settleType?.toUpperCase(),
    beneficiary_account_id: txnPayLoad?.beneAccountId,
    owner_account_id: txnPayLoad?.ownerId,
    confidential_transaction: isConfidential
      ? "Show only confidential"
      : "Hide confidential",
  });

  const handleCreateTransaction = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const hasImage = txnPayLoad?.mergeCategory?.some(
        (field) =>
          field?.data_type === "upload" && field?.value !== "Not given",
      );

      const uploadResponses = hasImage
        ? await Promise.all(
            Object.values(uploadedFiles).map((file) =>
              dispatch(uploadFile({ file })),
            ),
          )
        : [];

      const beneficiaryTxnData = {
        amount: txnPayLoad.amount,
        owner_account_id: txnPayLoad.ownerId,
        beneficiary_account_id: txnPayLoad.beneAccountId,
        category_id: selectedCategory?.id,
        additional_data: {
          data: buildAdditionalDataPayload(
            selectedCategory?.configuration?.data,
            additionalData,
            uploadResponses,
            hasImage,
          ),
        },
        payment_method: txnPayLoad.settleType.toUpperCase(),
        confidential_transaction: isConfidential,
        labels: uniq(txnPayLoad.labels, txnPayLoad.ownerLabels),
      };

      const response = await dispatch(
        beneficiaryTransactionsCreate({ beneficiaryTxnData }),
      );

      if (beneficiaryTransactionsCreate.fulfilled.match(response)) {
        const transactionId = response.payload?.transaction?.[0]?.txn_id;

        // Mark the old (rejected) transaction as refactored.
        // Only runs when we really have the old transaction id.
        if (isRefactorFlow && refactoredTxn?.txn_id) {
          await dispatch(
            beneficiaryTransactionsRecreate({ id: refactoredTxn.txn_id }),
          );
        }

        // Done with the recreate flow, don't let it leak into the next visit.
        clearRecreateSession();

        navigate(
          `/beneficiary_transaction_dashboard/create_beneficiary_transaction/success/${transactionId}`,
        );
      } else {
        navigate(
          "/beneficiary_transaction_dashboard/create_beneficiary_transaction/failed",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    hasAutoFillInteracted.current = false;
    if (isButtonDisabled) return;
    setIsButtonDisabled(true);

    try {
      // amount -> confirm step: run the duplicate check before moving on
      if (activeStep === TWO) {
        setTransactionLoading(true);
        setIsTxnLoading(true);

        try {
          const response = await dispatch(
            beneficiaryTransactionsViewAll(buildDuplicateCheckParams()),
          );

          const hasDuplicates = response?.payload?.transactions?.length > ZERO;

          setHasSimilarTransaction(hasDuplicates);

          if (hasDuplicates) {
            toast.dismiss();
            CommonToast({
              text: (
                <>
                  Similar transaction found. Click check <br />
                  to view.
                </>
              ),
              icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
              variant: "var(--error)",
              buttonNeeded: true,
              btnProps: {
                enabledBgColor: "var(--negative-card)",
                enabledTextColor: "var(--text-primary)",
                btnText: "Check",
                imgColor: "var(--text-primary)",
                handleFunction: async () => {
                  setIsSimilarModalOpen(true);
                },
                icon: ICONS.ARROW_RIGHT_WHITE,
                btnType: "button",
                isDisabled: false,
                hoverOption: "black",
              },
            });
          }

          changeStep(ONE, "next");
        } finally {
          setTransactionLoading(false);
          setIsTxnLoading(false);
        }

        return;
      }

      if (activeStep === lastStepIndex) {
        if (hasSimilarTransaction) setIsSimilarModalOpen(true);
        else await handleCreateTransaction();
        return;
      }

      changeStep(ONE, "next");
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const isFormDirty = () =>
    activeStep > ZERO ||
    txnPayLoad.ownerId !== null ||
    txnPayLoad.beneAccountId !== "" ||
    txnPayLoad.categoryIds !== "" ||
    txnPayLoad.categoryIds?.length > ZERO;

  const leavePage = () => {
    // Leaving without creating: drop the recreate marker.
    clearRecreateSession();
    navigate(-1);
  };

  const handleBack = () => {
    hasAutoFillInteracted.current = false;
    if (isButtonDisabled) return;

    if (activeStep === ZERO) {
      if (isFormDirty()) setIsConfirmLeavePopupOpen(true);
      else leavePage();
      return;
    }

    changeStep(-ONE, "previous");
  };

  /* ------------------------------- accounts ------------------------------- */

  const handleOpenDestinationAccountPopup = () => {
    setActiveAccountPopup("destination");
  };

  const handleOpenAssociatedAccountsPopup = () => {
    const tab = txnPayLoad?.pan ? "pan" : "bank";

    setActiveType(tab);
    setRemPan(tab === "pan" ? txnPayLoad?.pan || "" : "");
    setRemAccount(tab === "bank" ? txnPayLoad?.bankAccountNo || "" : "");
    setRemIFSC(tab === "bank" ? txnPayLoad?.bankAccountIFSC || "" : "");

    setActiveAccountPopup("associated");
  };

  const associatedAccountsText = [
    "This includes only active (approved, pending) accounts linked to the provided destination account.",
    "Click here to view and change the destination account, if required.",
  ];

  const handleCloseAccountPopup = () => {
    setActiveAccountPopup(null);
    setRemPan("");
    setRemAccount("");
    setRemIFSC("");
  };

  const handleOwnerSelection = (name, acc, ifsc, logo, ownerId) => {
    setTxnPayLoad((previousPayload) => ({
      ...previousPayload,
      ownerId,
      ownerAccountName: name,
      ownerBankAccountNo: acc,
      ownerBankIFSC: ifsc,
      ownerBankLogo: logo,
    }));
  };

  const isAssociatedPopup = activeAccountPopup === "associated";

  const handleDestinationAccountSelection = (dataItem) => {
    if (!dataItem?.id) return;

    const {
      id: beneAccountId,
      name: accountName,
      bank_acc_no: rawAccountNumber,
      ifsc_code: rawIFSC,
      bank_name: bankLogo,
      category_ids: categoryIds,
      pan_no: panNo,
      labels,
    } = dataItem;

    const accountNumber = rawAccountNumber?.trim() || "";
    const ifsc = rawIFSC?.trim() || "";

    const isRecreateFlow = mappingData === "renderData";
    const isAccountChanged =
      txnPayLoad?.beneAccountId && txnPayLoad.beneAccountId !== beneAccountId;
    const keepSelectedCategory = isRecreateFlow && !isAccountChanged;

    setAccountNumber(accountNumber);
    setIFSC(ifsc);
    setHasUserSelectedAccount(true);
    setCategory(
      (Array.isArray(categoryIds) ? categoryIds : []).map(
        (item) => item?.category_id ?? item?.id,
      ),
    );

    if (!keepSelectedCategory) {
      setSelectedCategory(null);
      setCatSelect(false);
      setInitialRefactorAdditionalDetail({});
    }

    setTxnPayLoad((prev) => ({
      ...prev,
      bankAccountName: accountName?.trim() || "",
      beneAccountId,
      bankAccountIFSC: ifsc,
      bankAccountNo: accountNumber,
      bankLogo,
      pan: panNo,
      labels: labels || [],
      selectedCategory: keepSelectedCategory ? selectedCategory : null,
      amount: hasReminderAmount ? String(parsedReminderAmount) : "",
      amountInWords: hasReminderAmount
        ? getAmountWords(parsedReminderAmount)
        : "",
      settleType: hasReminderAmount ? reminderSettleType : "",
      mergeCategory: null,
    }));

    if (hasReminderAmount) {
      setIsNeft(reminderSettleType === "neft");
      setIsRtgs(reminderSettleType === "rtgs");
    }

    if (txnData?.beneficiary_accounts?.[0]?.id !== beneAccountId) {
      setAccountSelect(false);
    }

    handleCloseAccountPopup();
  };

  /* -------------------------------- labels -------------------------------- */
  const handleCloseLabelModal = (finalLabels) => {
    setIsLabelModalOpen(false);
    if (!finalLabels) return;

    setTxnPayLoad((prev) => ({
      ...prev,
      ...splitLabels(finalLabels, prev.ownerLabels),
    }));
  };

  const handleCopyLabels = (prevLabels) =>
    setTxnPayLoad((prev) => ({
      ...prev,
      ...splitLabels(prevLabels, prev.ownerLabels),
    }));

  /* -------------------------------- amount -------------------------------- */
  const handleAmountChange = (input) => {
    const { name, value } = input?.target ? input.target : input;
    const trimmedValue = value?.toString()?.trim() ?? "";

    if (!trimmedValue) {
      setTxnPayLoad((prev) => ({ ...prev, amount: "", amountInWords: "" }));
      return;
    }
    if (trimmedValue[0] === "0") return;

    const numericValue = trimmedValue.replace(/[^0-9.]/g, "");

    if (
      name !== "amount" ||
      (!Validation.allowedAmount(numericValue) && numericValue.length)
    ) {
      return;
    }

    const settleType =
      parseFloat(numericValue) > RTGS_MIN_AMOUNT ? "rtgs" : "neft";
    const hasPaymentMethodChanged =
      (settleType === "neft" && isRtgs) || (settleType === "rtgs" && isNeft);

    if (hasPaymentMethodChanged) {
      setSlideAnimation(true);
      setTimeout(
        () => setSlideAnimation(false),
        PaymentMethodAnimationConfiguration.slideDuration,
      );
    }

    setIsNeft(settleType === "neft");
    setIsRtgs(settleType === "rtgs");
    setTxnPayLoad((prev) => ({
      ...prev,
      amount: numericValue,
      amountInWords: getAmountWords(numericValue),
      settleType,
    }));
  };

  const handleNeftClick = () => {
    if (Number(txnPayLoad?.amount) > RTGS_MIN_AMOUNT)
      showAmountLimitToast("less than");
  };

  const handleRtgsClick = () => {
    if (Number(txnPayLoad?.amount) <= RTGS_MIN_AMOUNT)
      showAmountLimitToast("more than");
  };

  const copyToClipboard = (title, data_type, value) => {
    if (navigator?.clipboard?.writeText === undefined) return;

    navigator.clipboard
      .writeText(value?.toString())
      .then(() => setCopyData({ title, data_type, value }))
      .catch((err) => console.error("Clipboard copy failed:", err));
  };

  /* ----------------------------- additional details ----------------------- */
  const handleUnassignNavigate = (id) =>
    navigate(
      `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/edit_categories/${id}`,
      { replace: false },
    );

  const handleCategorySelect = (categoryId) => {
    const categoryData = safeCategories.find((c) => c.id === categoryId);
    const isAssigned = category?.includes(categoryId);

    if (!categoryData || categoryData?.active !== "Y" || !isAssigned) {
      clearSelectedCategory();
      setAdditionalData({});
      return;
    }

    // First render: show loader and clear the old fields
    setIsCategoryLoading(true);
    setTxnPayLoad((prev) => ({ ...prev, mergeCategory: null }));
    resetAdditionalFields();

    // Second render: set the new category + fields
    requestAnimationFrame(() => {
      applySelectedCategory(categoryData);
      setIsCategoryLoading(false);
    });
  };

  const handleInputChange = (event) => {
    event?.preventDefault?.();

    const name = event?.target?.name ?? event?.name;
    const value = event?.target?.value ?? event?.value;

    const field = txnPayLoad?.selectedCategory?.configuration?.data?.find(
      (item) => item.name === name,
    );
    if (!field) return;

    const isMonthYearCleared =
      field.data_type === "month_year" && !(value?.month && value?.year);

    if (isMonthYearCleared) {
      setInitialRefactorAdditionalDetail((prev) => ({
        ...prev,
        [name]: { ...prev[name], isCopyDirect: false, isCopyClick: false },
      }));
    }

    setAdditionalData((prev) => ({
      ...prev,
      [name]: toAdditionalDataValue(field.data_type, value),
    }));
  };

  const handleFieldChange = (name, value, type) =>
    handleInputChange({
      target: { name, value, data_typeChan: type },
    });

  const handleQuantityChange = (value, fieldName, dataType, key) => {
    if (value < 0 || String(value).length > 15) return;

    const quantity = value === "" ? "" : Number(value);

    setInitialRefactorAdditionalDetail((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        isCopyDirect: false,
        isCopyClick: false,
      },
    }));
    setQuantities((prev) => ({ ...prev, [key]: quantity }));

    handleFieldChange(fieldName, quantity, dataType);
  };

  const handleTimeConfirm = (
    fieldName,
    dataType,
    hourShow,
    minuteShow,
    meridiemShow,
  ) =>
    handleFieldChange(
      fieldName,
      `${hourShow}:${minuteShow} ${meridiemShow}`,
      dataType,
    );

  /* ------------------------------ stepper data ---------------------------- */
  const reminderText = isReminderFlow
    ? `Reminder remark: ${location?.state?.txnDetails?.reminderRemark || "-"}`
    : "";

  const dataArray = steps.map((stepLabel, index) => {
    const isLastStep = index === lastStepIndex;
    const isTxnSubmitting = isLastStep && isSubmitting;
    const isAmountStep = index === TWO;

    return {
      ...COMMON_STEPPER_CONFIG,
      previousText: index === ZERO ? "Cancel" : "Previous",
      previousIsDisabled: isTxnSubmitting,
      nextText: isLastStep
        ? hasSimilarTransaction
          ? "Check"
          : "Create"
        : "Next",

      // Show the loader on the Amount step and the final Create step.
      nextLoaderNeeded: isLastStep,
      nextIsLoading: isLastStep ? isSubmitting : isAmountStep && isTxnLoading,
      nextIsEnabled: isLastStep
        ? !isSubmitting
        : isNextEnabled && !isButtonDisabled,

      nextEnableBgColor: isLastStep
        ? hasSimilarTransaction
          ? "var(--emphasis)"
          : "var(--positive-act)"
        : "var(--text-primary)",
      nextDisableBgColor: isLastStep
        ? "var(--positive-act)"
        : "var(--text-disabled)",
      nextEnableTextColor: isLastStep
        ? "var(--text-primary)"
        : "var(--primary-bg)",
      nextDisableTextColor: "var(--text-primary)",
      stepperText: stepLabel,
      extraInfoSubText: reminderText,
      extraInfoShow: isReminderFlow,
    };
  });

  /* ------------------------------- renderers ------------------------------ */
  const renderCreateTxn = () => {
    switch (activeStep) {
      case 0:
        return (
          <BeneficiaryTransactionStepAccount
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            openOwner={openOwner}
            setOpenOwner={setOpenOwner}
            handleOwnerSelection={handleOwnerSelection}
            handleOpenDestinationAccountPopup={
              handleOpenDestinationAccountPopup
            }
            handleOpenAssociatedAccountsPopup={
              handleOpenAssociatedAccountsPopup
            }
            shouldShowAssociatedAccounts={shouldShowAssociatedAccounts}
            isConfidential={isConfidential}
            setIsConfidential={setIsConfidential}
            associatedAccountsText={associatedAccountsText}
          />
        );
      case 1:
        return (
          <BeneficiaryTransactionStepAdditionalDetails
            txnPayLoad={txnPayLoad}
            beneAccountId={txnPayLoad?.beneAccountId}
            category={category}
            selectedCategory={selectedCategory}
            unassignCategories={unassignCategories}
            unassignlength={unassignlength}
            disableCategories={disableCategories}
            isCategoryLoading={isCategoryLoading}
            isCategorySelected={isCategorySelected}
            handleCategorySelect={handleCategorySelect}
            handleUnassignNavigate={handleUnassignNavigate}
            additionalData={additionalData}
            setAdditionalData={setAdditionalData}
            hyperLink={hyperLink}
            setHyperLink={setHyperLink}
            timeData={timeData}
            setTimeData={setTimeData}
            quantities={quantities}
            setQuantities={setQuantities}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            selectedValue={selectedValue}
            setSelectedValue={setSelectedValue}
            selectedValueMonth={selectedValueMonth}
            setSelectedValueMonth={setSelectedValueMonth}
            previewImages={previewImages}
            setPreviewImages={setPreviewImages}
            initialRefactorAdditionalDetail={initialRefactorAdditionalDetail}
            setInitialRefactorAdditionalDetail={
              setInitialRefactorAdditionalDetail
            }
            selectedLabelsCreate={selectedLabels}
            setShowLabelModal={setIsLabelModalOpen}
            handleFieldChange={handleFieldChange}
          />
        );
      case 2:
        return (
          <BeneficiaryTransactionStepAmount
            txnPayLoad={txnPayLoad}
            isNeft={isNeft}
            isRtgs={isRtgs}
            copyData={copyData}
            setCopyData={setCopyData}
            txnLoading={isTxnLoading}
            slideAnimation={slideAnimation}
            handleNeftClick={handleNeftClick}
            handleRtgsClick={handleRtgsClick}
            handleAmountChange={handleAmountChange}
          />
        );
      case 3:
        return (
          <BeneficiaryTransactionStepConfirm
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            isConfidential={isConfidential}
            previewImages={previewImages}
            allLabels={allLabels}
            uploadedFiles={uploadedFiles}
          />
        );
      default:
        return null;
    }
  };

  const renderRefactorSteps = (stepOverride) => {
    switch (stepOverride ?? activeStep) {
      case 0:
        return (
          <BeneficiaryRecreateTxnStepSourceDestination
            setAccountSelect={setAccountSelect}
            setSourceSelect={setSourceSelect}
            setMapingData={setMappingData}
            txnData={txnData}
            txnPayload={txnPayLoad}
            beneCat={txnData?.category_id}
            setIsConfidential={setIsConfidential}
            isConfidential={isConfidential}
            accountSelect={accountSelect}
            isAutoFilledSource={isAutoFilledSource}
            setIsAutoFilledSource={setIsAutoFilledSource}
          />
        );
      case 1:
        return (
          <BeneficiaryRecreateTxnStepAdditionalDetails
            setCatSelect={setCatSelect}
            beneCat={txnCategoryName}
            beneCatId={txnCategoryId}
            setMapingCat={setMappingCat}
            selectedCategory={selectedCategory}
            txnPayLoad={txnPayLoad}
            onCopyLabels={handleCopyLabels}
            txnData={txnData}
            beneficiaryTransactionsDetailsData={
              beneficiaryTransactionsDetailsData
            }
            initialRefactorAdditionalDetail={initialRefactorAdditionalDetail}
            setInitialRefactorAdditionalDetail={
              setInitialRefactorAdditionalDetail
            }
            setSelectedValue={setSelectedValue}
            setSelectedValueMonth={setSelectedValueMonth}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            setTimeData={setTimeData}
            additionalData={additionalData}
            setAdditionalData={setAdditionalData}
            setQuantities={setQuantities}
            assignedCategories={category}
            categories={categories}
          />
        );
      case 2:
        return (
          <BeneficiaryRecreateTxnStepAmountPaymentMethod
            txnData={txnData}
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            setIsNeft={setIsNeft}
            setIsRtgs={setIsRtgs}
            isAmountCopied={isAmountCopied}
            setIsAmountCopied={setIsAmountCopied}
            copyToClipboard={copyToClipboard}
          />
        );
      default:
        return null;
    }
  };

  /* -------------------------------- effects ------------------------------- */
  // initial category list when the user arrives with an account preselected
  useEffect(() => {
    const { beneAccountId, pan, bankAccountIFSC, bankAccountNo } =
      txnPayLoad || {};

    if (!beneAccountId || !hasUserSelectedAccount) return;
    if (prevBeneAccountRef.current === beneAccountId) return;

    prevBeneAccountRef.current = beneAccountId;

    dispatch(getAllCategories({ bene_account_id: beneAccountId }));

    if (pan) dispatch(panLookUp({ pan_number: pan }));

    if (bankAccountIFSC && bankAccountNo) {
      dispatch(
        bankLookUp({ ifsc: bankAccountIFSC, bank_account_no: bankAccountNo }),
      );
    }
  }, [txnPayLoad?.beneAccountId, hasUserSelectedAccount]);

  useEffect(() => {
    setHaveBankAssociate(bankLookupData?.total_count || 0);
    setHavePANAssociate(panLookupData?.total_count || 0);
  }, [panLookupData, bankLookupData, dispatch]);

  // recent-transaction labels (used for the label suggestions)
  useEffect(() => {
    if (sessionStorage.getItem("beneficiaryTxnLabels")) return;

    dispatch(
      beneficiaryTransactionsViewAll({
        page_no: ONE,
        page_item_count: PaginationConfiguration.paginationSmall,
      }),
    );
  }, []);

  // seed the label suggestions
  useEffect(() => {
    setAvailableLabels(
      uniq(
        txnPayLoad?.labels,
        txnPayLoad?.ownerLabels,
        readSessionJson("beneficiaryTxnLabels", []),
        readSessionJson("beneficiaryAccountLabels", []),
      ),
    );
  }, []);

  // owner labels first, then the rest
  useEffect(() => {
    if (!txnPayLoad) return;

    const ownerLabels = txnPayLoad?.ownerLabels || [];
    const labels = txnPayLoad?.labels || [];

    setSelectedLabels([
      ...ownerLabels,
      ...labels.filter((label) => !ownerLabels.includes(label)),
    ]);
  }, [txnPayLoad?.ownerLabels, txnPayLoad?.labels]);

  // entered values -> mergeCategory
  useEffect(() => {
    if (!additionalData) return;

    setTxnPayLoad((prev) => ({
      ...prev,
      mergeCategory: buildMergeCategory(
        txnPayLoad.selectedCategory?.configuration?.data,
        additionalData,
      ),
    }));
  }, [additionalData, txnPayLoad.selectedCategory]);

  // category loader: stop once the category fields are ready
  useEffect(() => {
    if (!isCategoryLoading) return;

    if (selectedCategory?.configuration?.data && txnPayLoad?.mergeCategory) {
      setIsCategoryLoading(false);
    }
  }, [isCategoryLoading, selectedCategory, txnPayLoad?.mergeCategory]);

  // enable / disable "Next"
  useEffect(() => {
    const {
      selectedCategory: payloadCategory,
      amount,
      settleType,
    } = txnPayLoad;

    const configData = payloadCategory?.configuration?.data || [];
    const mergeCategory = txnPayLoad?.mergeCategory || [];

    const mandatoryFields = configData.filter((f) => f?.mandatory === "Y");
    const filledMandatoryFields = mergeCategory.filter(
      (f) => f?.mandatory === "Y" && isMandatoryValueFilled(f),
    );
    const isMandatoryFieldsFilled =
      mandatoryFields.length === filledMandatoryFields.length;

    const hasHyperlink = mergeCategory.some(
      (f) => f?.data_type === "hyperlink",
    );
    const areHyperlinksValid = mergeCategory
      .filter((f) => f?.data_type === "hyperlink" && f?.value !== "")
      .every((f) => isValidHttpUrl(f?.value));

    const isAmountValid = amount > ZERO;
    const isRtgsValid = settleType !== "rtgs" || amount >= RTGS_MIN_AMOUNT;
    const isSettleTypeValid =
      !!settleType &&
      !(
        (settleType === "rtgs" && amount <= RTGS_MIN_AMOUNT) ||
        (settleType === "neft" && amount > RTGS_MIN_AMOUNT)
      );

    const stepValidity = [
      !!txnPayLoad?.ownerId && !!txnPayLoad?.beneAccountId,
      !!payloadCategory &&
        isMandatoryFieldsFilled &&
        (!hasHyperlink || areHyperlinksValid),
      isAmountValid && isRtgsValid && isSettleTypeValid,
      true,
    ];

    setIsNextEnabled(stepValidity[activeStep] ?? false);
  }, [activeStep, txnPayLoad, hyperLink]);

  // Prefill the amount and payment method when creating a transaction from a reminder.
  useEffect(() => {
    if (!hasReminderAmount) return;

    setTxnPayLoad((prev) => ({
      ...prev,
      amount: String(parsedReminderAmount),
      amountInWords: getAmountWords(parsedReminderAmount),
      settleType: reminderSettleType,
    }));

    setIsNeft(reminderSettleType === "neft");
    setIsRtgs(reminderSettleType === "rtgs");
  }, [hasReminderAmount, parsedReminderAmount, reminderSettleType]);

  useEffect(() => {
    setIsAmountCopied(Number(txnData?.amount) === Number(txnPayLoad?.amount));
  }, [txnData?.amount, txnPayLoad?.amount]);

  // recreate flow: previous transaction details
  useEffect(() => {
    if (!isRefactorFlow || !refactoredTxn?.txn_id) return;

    dispatch(beneficiaryTransactionsDetailsById({ id: refactoredTxn.txn_id }));
  }, [dispatch, isRefactorFlow, refactoredTxn?.txn_id]);

  useEffect(() => {
    if (txnData?.owner_account_bank_acc_no) {
      dispatch(
        ownerAccountsViewAll({
          bank_account_no: txnData?.owner_account_bank_acc_no,
        }),
      );
    }
  }, [activeStep, txnData?.owner_account_bank_acc_no, dispatch]);

  useEffect(() => {
    if (beneficiaryTransactionsDetailsLoading) return;

    const previousDetails = {};
    const previousData =
      beneficiaryTransactionsDetailsData?.transaction?.[0]?.additional_data
        ?.data || [];

    previousData.forEach(({ name, value, data_type: dataType }) => {
      const isEmpty =
        value === null ||
        value === "" ||
        value === undefined ||
        value?.length === ZERO;
      if (isEmpty) return;

      previousDetails[name] = ["hyperlink", "string", "numeric"].includes(
        dataType,
      )
        ? { isCopyClick: false, isPaste: {}, name, data: value }
        : { isCopyDirect: false, name, data: value };
    });

    setInitialRefactorAdditionalDetail(previousDetails);
  }, [
    beneficiaryTransactionsDetailsData,
    beneficiaryTransactionsDetailsLoading,
  ]);

  // recreate flow: copy destination account
  useEffect(() => {
    if (mappingData !== "renderData" || !accountSelect) return;

    const accountNumber = txnData?.beneficiary_account_bank_acc_no;
    const ifsc = txnData?.beneficiary_account_ifsc_code;

    if (!accountNumber || !ifsc) {
      setMappingData(null);
      setSourceSelect(false);
      return;
    }

    dispatch(
      getAllBeneficiaryAccounts({
        bank_account_no: accountNumber,
        ifsc: ifsc.toUpperCase(),
      }),
    )
      .then((response) => {
        const account = response?.payload?.beneficiary_accounts?.[0];

        const selectedAccount = {
          ...account,
          id: account?.id ?? txnData?.beneficiary_account_id,
          name: account?.name ?? txnData?.beneficiary_account_name ?? "",
          bank_acc_no: account?.bank_acc_no ?? accountNumber,
          ifsc_code: account?.ifsc_code ?? ifsc,
          bank_name:
            account?.bank_name ?? txnData?.beneficiary_account_bank_name ?? "",
          category_ids: account?.category_ids ?? [],
          pan_no: account?.pan_no ?? txnData?.beneficiary_account_pan ?? "",
          labels: account?.labels ?? txnData?.labels ?? [],
        };

        if (!selectedAccount.id) {
          CommonToast({
            text: "Unable to find destination account.",
            icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
          return;
        }

        // Map the copied destination account directly.
        // Do not open the destination account popup here.
        handleDestinationAccountSelection(selectedAccount);
      })
      .finally(() => {
        setMappingData(null);
        setSourceSelect(false);
      });
  }, [mappingData, accountSelect]);

  // recreate flow: copy source account
  useEffect(() => {
    if (mappingData !== "renderSource" || !sourceSelect) return;

    handleOwnerSelection(
      txnData?.owner_account_bank_name,
      txnData?.owner_account_bank_acc_no,
      txnData?.owner_account_ifsc_code,
      txnData?.owner_account_bank_name,
      txnData?.owner_account_id,
      txnData?.owner_account_balance,
      txnData?.owner_account_balance_last_fetched_at_in_words,
    );

    setMappingData(null);
    setSourceSelect(false);
  }, [mappingData, sourceSelect]);

  // recreate flow: copy category
  useEffect(() => {
    if (mappingCat !== "renderCat" || !catSelect) return;

    const finish = () => {
      setMappingCat(null);
      setCatSelect(false);
    };

    if (!txnCategoryId) {
      finish();
      return;
    }

    const categoryData = categories?.find(
      (cat) => String(cat.id) === String(txnCategoryId),
    );
    const isAssigned =
      !!categoryData &&
      (category || []).some((id) => String(id) === String(categoryData.id));

    if (!categoryData || !isAssigned || categoryData?.active !== "Y") {
      clearSelectedCategory();
      finish();
      return;
    }

    applySelectedCategory(categoryData);
    resetAdditionalFields();
    finish();
  }, [mappingCat, catSelect]);

  // recreate flow: "copy" button on a field -> fill the matching input
  useEffect(() => {
    const { title, value, data_type: dataType } = copyData || {};
    if (value === "" || title === "") return;

    const fieldKey = `${title}_${dataType}`;

    switch (dataType) {
      case "time": {
        const [hourShow, rest] = value.split(":");
        const [minuteShow, meridiemShow] = rest.split(" ");

        handleTimeConfirm(title, dataType, hourShow, minuteShow, meridiemShow);
        setTimeData((prev) => ({
          ...prev,
          [fieldKey]: { hours: hourShow, minutes: minuteShow },
        }));
        resetCopyData();
        break;
      }

      case "date": {
        // try a direct parse first, then strip the comma as a fallback
        let parsedDate = new Date(value);
        if (isNaN(parsedDate.getTime())) {
          parsedDate = new Date(value.replace(",", ""));
        }

        if (!isNaN(parsedDate.getTime())) {
          // formatted string goes to additionalData, Date object to the calendar
          handleFieldChange(title, formatUIDate(parsedDate), dataType);
          setStartDate((prev) => ({ ...prev, [fieldKey]: parsedDate }));
        }
        resetCopyData();
        break;
      }

      case "quantity":
        handleQuantityChange(value || 0, title, dataType, fieldKey);
        resetCopyData();
        break;

      case "toggle":
        handleFieldChange(title, value, dataType);
        resetCopyData();
        break;

      case "month_year": {
        handleFieldChange(title, value, dataType);

        const [monthStr, yearStr] = value.split("/");
        const monthName = new Date(
          2000,
          parseInt(monthStr, 10) - 1,
        ).toLocaleString("default", { month: "long" });

        setSelectedValueMonth((prev) => ({
          ...prev,
          [fieldKey]: { month: monthName, year: parseInt(yearStr, 10) },
        }));
        resetCopyData();
        break;
      }

      case "from_year_to_year":
        handleFieldChange(title, value, dataType);
        setSelectedValue((prev) => ({
          ...prev,
          [fieldKey]: { from: value?.from, to: value?.to },
        }));
        resetCopyData();
        break;

      case "date_range":
        resetCopyData();
        break;

      default:
        break;
    }
  }, [copyData]);

  // recreate flow: pasted hyperlink -> fill the hyperlink input
  useEffect(() => {
    if (!initialRefactorAdditionalDetail) return;

    Object.entries(initialRefactorAdditionalDetail).forEach(
      ([key, fieldMeta]) => {
        if (!(fieldMeta?.isCopyClick && fieldMeta?.isPaste)) return;
        if (fieldMeta.isPaste.data_type !== "hyperlink") return;

        const value = fieldMeta.isPaste.value ?? "";

        setHyperLink((prev) => ({ ...prev, [`${key}_hyperlink`]: value }));
        handleFieldChange(key, value, "hyperlink");
        setInitialRefactorAdditionalDetail((prev) => ({
          ...prev,
          [key]: { ...prev[key], isCopyClick: false, isPaste: {} },
        }));
      },
    );
  }, [initialRefactorAdditionalDetail]);

  // autofill panel: block hover highlight while it is animating open
  useEffect(() => {
    if (autoFill) {
      setIsHoverEnabled(false);
      return;
    }

    const timer = setTimeout(
      () => setIsHoverEnabled(true),
      AUTOFILL_HOVER_DELAY,
    );
    return () => clearTimeout(timer);
  }, [autoFill]);

  useEffect(() => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [activeStep]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;

      setIsMobile(mobile);
      if (!mobile) {
        setReCreateFlow(false);
        setAutoFill(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const updateHeight = () =>
      setStepperHeight(
        stepperHeightRef?.current?.firstElementChild?.offsetHeight,
      );

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [stepperHeight]);

  /* --------------------------------- render ------------------------------- */
  return (
    <>
      {activeAccountPopup && (
        <BeneficiaryAccountViewAllPopup
          typepop="popup"
          accountType="beneficiary"
          onClose={handleCloseAccountPopup}
          handleButtonClick={handleDestinationAccountSelection}
          activeType={activeType}
          setActiveType={setActiveType}
          txnPayLoad={txnPayLoad}
          remPan={isAssociatedPopup && activeType !== "pan" ? "" : remPan}
          remAccount={
            isAssociatedPopup && activeType !== "bank" ? "" : remAccount
          }
          remIFSC={isAssociatedPopup && activeType !== "bank" ? "" : remIFSC}
          setRemPan={setRemPan}
          setRemAccount={setRemAccount}
          setRemIFSC={setRemIFSC}
          showModalPopup={false}
          createDisabled={false}
          {...(isAssociatedPopup
            ? {
                flow: "beneTxn",
                sourceDestiAcc: false,
                BeneTxnAssocAcct: true,
                ispanbankRequired: true,
                typePanBank: "BeneAccPanBankSwitch",
                associatepanNumber: txnPayLoad?.pan,
                associateIFSC: txnPayLoad?.bankAccountIFSC,
                associateaccountNumber: txnPayLoad?.bankAccountNo,
              }
            : {
                type: "viewAll",
                id: txnPayLoad?.beneAccountId,
                beneAccountId: txnPayLoad?.beneAccountId,
                flow: "beneficiaryTxn",
                flowType: "scheduleCreate",
                sourceDestiAcc: true,
                typePanBank: activeType,
              })}
        />
      )}

      {isLabelModalOpen && (
        <div className="fixed left-left-base w-width-full top-top-base h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity flex items-center justify-center z-max-zindex">
          <CommonAssignLabelPopup
            headerTitle="Assign/unassign labels:"
            headersubtext="Labels will be used to generate statistics for the associated transactions."
            closehandleFunction={handleCloseLabelModal}
            closeisDisabled={false}
            labels={availableLabels}
            setLabels={setAvailableLabels}
            isLabelPopupOpen={isLabelModalOpen}
            setPayload={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            selectedLabelsCreate={selectedLabels}
            setSelectedLabelsCreate={setSelectedLabels}
            labelActionDispatchedRef={labelActionDispatchedRef}
            loading={isLabelLoading}
            setLoading={setIsLabelLoading}
            type="create"
          />
        </div>
      )}

      {isBankingHoursPopupOpen && (
        <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop bg-opacity-dark-opacity justify-center z-z-index-modal">
          <SmallPopupWithBulletPoints
            headerTitle="Why transaction cannot be processed?"
            headerFontWeight="semibold"
            headerFontColor="negative-act"
            textSize="font-md"
            bulletPoints={[
              `Any transaction created or approved after banking hours i.e. between ${getOutsideBankingHoursLabel()} will be processed on the next bank working day.`,
              "Payments are time-critical (e.g., salary, vendor payment, or bill payment), consider the possible processing delay before proceeding.",
            ]}
            closeIconImage={ICONS?.CLOSE}
            closeModal={() => setIsBankingHoursPopupOpen(false)}
          />
        </div>
      )}

      {transactionLoading && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-light-opacity">
          <div>
            <Loader type="bulk_upload" />
            <p className="font-semibold text-font-lg text-text-primary mt-margin-sm">
              Validating the data
            </p>
          </div>
        </div>
      )}

      {isSimilarModalOpen && (
        <BeneficiaryTransactionViewAllPopup
          isOpen={isSimilarModalOpen}
          onClose={() => setIsSimilarModalOpen(false)}
          headerTitle="Similar transactions found"
          headerSubtitle="Transactions with similar details have already been created. Proceeding will result in a duplicate transaction."
          headerIcon={ICONS.ALERT}
          showHeaderIcon={true}
          showFooter={true}
          footerTitle="Proceed with creating this transaction?"
          showFooterButton={true}
          footerButtonText="Proceed with caution"
          onFooterButtonClick={() => {
            setIsSimilarModalOpen(false);
            handleCreateTransaction();
          }}
          footerButtonBackgroundColor="var(--negative-act)"
          footerButtonTextColor="var(--text-primary)"
          footerButtonHoverOption="dark"
          footerButtonOutlineColor=""
          className="fixed top-left-base left-left-base w-screen h-screen z-z-index-max bg-PopUp-BackDrop bg-opacity-dark-opacity"
          contentProps={{
            bene_account_name: txnPayLoad?.bankAccountName || "NA",
            bene_account_id: txnPayLoad?.beneAccountId || null,
            bene_account_confidential: txnPayLoad?.beneAccountId || null,
            onClose: () => setIsSimilarModalOpen(false),
            queryParams: buildDuplicateCheckParams(),
            similarTxnType: "similarTxn",
            popup: "modalType",
          }}
        />
      )}

      {isConfirmLeavePopupOpen && (
        <div
          className="fixed z-z-index-max flex items-center justify-center top-top-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <SmallPopup
            headerTitle={globalText?.LEAVE_PAGE_WARNING_HEADER}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-lg"
            subtext={globalText?.EXIT_CURRENT_FLOW_NOTE}
            btnOneText={globalText?.STAY}
            btnOneHandleFunction={() => setIsConfirmLeavePopupOpen(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText={globalText?.LEAVE}
            btnTwoHandleFunction={() => {
              setIsConfirmLeavePopupOpen(false);
              leavePage();
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
          />
        </div>
      )}

      <div
        className={`w-width-full flex items-center flex-col bg-primary-bg overflow-y-auto ${
          openOwner || activeAccountPopup
            ? "h-[var(--height-full)]"
            : "h-[var(--full-height)]"
        }`}
      >
        <div className="w-width-full hidden Qsmd:block h-navbar-breadcrumb-height Qlg:bg-transparent bg-primary-nav flex-shrink-0"></div>
        <div
          style={isWindow && isReminderFlow ? { minHeight: stepperHeight } : {}}
          className={`fixed Qsmd:relative left-left-base z-z-index-loader flex justify-center items-center flex-col lg:flex-col-reverse w-width-full lg:py-padding-base bottom-bottom-base top-auto lg:top-top-base lg:bottom-auto Qsmd:bg-transparent shrink-0
         ${isReminderFlow ? "Qsmd:min-h-topnav-bottom-height min-h-stepper-moble-height" : "Qsmd:min-h-[calc(var(--stepper-height)+var(--icon-sm))] min-h-stepper-moble-height Qsmd:bg-transparent"}`}
        >
          {isMobile && (isReminderFlow || shouldShowRecreatePanel) && (
            <div className="fixed bottom-bottom-base w-width-full">
              <div
                className={`relative ${activeStep === lastStepIndex ? "hidden" : "bg-primary-nav-act global-hover-white z-z-index-base cursor-pointer"}`}
              >
                <div
                  className="Refactor_responsive_Bottomdiv w-width-full p-padding-md"
                  onClick={() => {
                    if (isReminderFlow) {
                      if (canToggleReminderBar) setOpenBar(!openBar);
                    }
                    setRefactorDisplayStep(activeStep);
                    setReCreateFlow(true);
                  }}
                >
                  <div className="flex justify-between">
                    <p className="font-semibold text-text-primary text-font-xs">
                      {isReminderFlow
                        ? "Reminder remark"
                        : "Copy or autofill from previous transactions details"}
                    </p>
                    <div className="">
                      <SVGComponent
                        url={ICONS?.ARROW_RIGHT_WHITE}
                        color={"var(--text-primary)"}
                        height={"var(--icon-sm)"}
                        width={"var(--icon-sm)"}
                        alt="Arrow"
                      />
                    </div>
                  </div>
                </div>
                <div className="h-stepper-moble-height w-width-full block Qsmd:hidden flex-shrink"></div>
              </div>
            </div>
          )}

          {(openBar || reCreateFlow) && (
            <div className="fixed top-top-base left-left-base w-width-full h-height-full bg-primary-bg z-z-index-max translate-y-0 ease-in-out flex flex-col">
              <TopNavComponent
                topNavHeader={
                  isReminderFlow
                    ? "Beneficiary transaction"
                    : "Create transaction"
                }
                topNavSubText={
                  isReminderFlow
                    ? steps[activeStep] || ""
                    : "Previous transaction details"
                }
                isFormattingNotRequired
              />
              {isReminderFlow ? (
                <section className="reminder-flow-message-container-bene overflow-x-hidden">
                  <span className="text-font-xs text-text-primary font-sans Qsmd:hidden">
                    Reminder remark
                  </span>
                  <p className="text-font-xs text-text-primary font-sans font-normal pt-padding-sm Qsmd:pt-padding-base break-words">
                    {location?.state?.txnDetails?.reminderRemark}
                  </p>
                </section>
              ) : (
                <>{renderRefactorSteps(refactorDisplayStep)}</>
              )}

              <div>
                <button
                  className="w-full h-auto flex items-center justify-center bg-text-primary cursor-pointer z-z-index-toast py-padding-sm fixed bottom-bottom-base left-left-base Qsmd:hidden transition-all duration-animation-medium ease-in-out"
                  onClick={() => {
                    if (isReminderFlow) setOpenBar(false);
                    if (reCreateFlow) setReCreateFlow(false);
                  }}
                >
                  <span className="text-font-md font-sans font-semibold text-primary-card">
                    Back
                  </span>
                </button>
                <div className="border-transparent border-b border-t py-padding-md"></div>
              </div>
            </div>
          )}

          <div
            ref={stepperHeightRef}
            className={` flex-col items-center justify-center ${openBar ? "hidden Qsmd:flex" : "flex"}`}
          >
            <PrimaryStepper
              activeStep={activeStep}
              handleForwardAction={handleNext}
              handleBackwardAction={handleBack}
              dataArray={dataArray}
            />
          </div>
        </div>

        {shouldShowRecreatePanel && (
          <div
            className={`Qsmd:block hidden w-width-full rounded-b-border-radius-md lg:max-w-tab-width-normal relative group top-[calc(var(--negative-global-var)*var(--padding-md))] ${isRefactorFlow ? "Qsmd:mb-[calc(var(--negative-global-var)*var(--margin-md))]" : ""}`}
          >
            <div className="lg:h-stepper-header qmd:block hidden"></div>
            {isRefactorFlow && (
              <div
                className={`Qmd:block hidden relative w-width-full py-padding-sm bg-primary-card max-w-tab-width-normal z-z-index-base ${isHoverEnabled ? "group-hover:bg-primary-nav-act" : ""} ${activeStep === lastStepIndex ? "rounded-b-border-radius-md" : ""}`}
              ></div>
            )}
            <div
              className={`w-width-full bg-primary-card relative lg:max-w-tab-width-normal rounded-b-border-radius-md justify-center
                ${isRefactorFlow ? "mt-margin-base" : "Qmd:mt-margin-base"} ${isMobile || activeStep === lastStepIndex ? "hidden" : "flex"}
                ${
                  autoFill ? "h-auto" : !isRefactorFlow ? "h-height-base" : ""
                }`}
            >
              <div
                className={`flex w-width-full ${autoFill ? "items-end" : "items-center pb-padding-base"} ${!isRefactorFlow ? "hidden" : ""}`}
              >
                <div className="flex flex-col w-width-full">
                  <div
                    className={`flex w-width-full justify-center ${refactorDisplayStep === ONE ? "pl-padding-md" : "px-padding-md"}`}
                  >
                    <div
                      className={`autofill-wrapper w-width-full !hidden Qmd:!flex ${
                        !hasAutoFillInteracted.current
                          ? ""
                          : autoFill
                            ? "open-wrap"
                            : "close-wrap"
                      }`}
                      onAnimationEnd={() => {
                        if (!autoFill) setRefactorDisplayStep(activeStep);
                      }}
                    >
                      {renderRefactorSteps(refactorDisplayStep)}
                    </div>
                  </div>
                  {autoFill && (
                    <div className="border border-primary-bg mx-margin-md"></div>
                  )}
                  {beneficiaryTransactionsDetailsLoading ? (
                    <div className="w-width-full flex justify-center relative global-hover-white z-z-index-base py-padding-sm rounded-b-border-radius-md">
                      <Loader
                        height="var(--loader-sm)"
                        width="var(--loader-sm)"
                      />
                    </div>
                  ) : (
                    <div
                      className="flex items-center justify-center gap-gap-xs py-padding-sm rounded-b-border-radius-md relative global-hover-white z-z-index-base cursor-pointer"
                      onClick={() => {
                        hasAutoFillInteracted.current = true;
                        if (!autoFill) setRefactorDisplayStep(activeStep);
                        setAutoFill(!autoFill);
                      }}
                    >
                      <p className="text-text-primary text-sm mr-margin-xs cursor-pointer">
                        Copy or autofill from previous transactions details
                      </p>

                      <div className="">
                        <SVGComponent
                          url={
                            autoFill
                              ? ICONS?.ARROW_UP_WHITE
                              : ICONS?.ARROW_DOWN_WHITE
                          }
                          alt="auto fill"
                          color={"var(--text-primary)"}
                          height={"var(--icon-sm)"}
                          width={"var(--icon-sm)"}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="fixed top-top-base left-left-base mt-margin-base w-width-full rounded-bl-border-radius-md rounded-br-border-radius-md z-z-index-header bg-primary-nav shadow-box-shadow-bottom lg:hidden block">
          <TopNavComponent
            topNavHeader="Beneficiary transaction"
            topNavSubText={
              reCreateFlow
                ? "Previous transaction details"
                : steps[activeStep] || ""
            }
            isFormattingNotRequired
          />
        </div>
        <div className="h-loader-lg Qsmd:hidden block flex-shrink-0"></div>
        <div className="overflow-hidden h-full flex flex-col items-center relative w-width-full">
          <div
            ref={scrollContainerRef}
            className={`w-width-full h-height-full mb-margin-base overflow-y-auto ${
              (activeStep === ZERO && stepDirection === "next") ||
              isStepAnimationComplete
                ? ""
                : stepAnimationClass
            }`}
            style={{ scrollbarWidth: "none" }}
          >
            {shouldShowBankingHoursAlert && (
              <div className="max-w-tab-width-normal Qlg:mx-auto pt-padding-md Qlg:px-padding-base px-padding-md w-width-full">
                <CommonAlertCard
                  title="Functioning outside banking hours"
                  texts="Transactions will be processed automatically during the next bank working day. Learn more"
                  highlightedText={["Learn more"]}
                  onHighlightedTextClick={(event, index) => {
                    if (index === 0) setIsBankingHoursPopupOpen(true);
                  }}
                />
              </div>
            )}

            {renderCreateTxn()}
          </div>
          {isRefactorFlow || reCreateFlow ? (
            <div
              className={`${
                activeStep === lastStepIndex
                  ? "h-stepper-moble-height"
                  : "h-[calc(var(--mobile-create-header)+var(--stepper-moble-height))]"
              } flex-shrink-0 block Qsmd:hidden`}
            />
          ) : (
            <>
              {isReminderFlow && activeStep !== lastStepIndex && (
                <div className="h-mobile-create-header flex-shrink-0 block Qsmd:hidden" />
              )}
              <div className="h-stepper-moble-height flex-shrink-0 block Qsmd:hidden" />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BeneficiaryTransactionCreate;
